import {format} from 'date-fns'
import {Lang} from 'yougile-lang'
import {
  logger,
  ObjUtil,
  relativeTimeToTimestamp,
  saveAsXlsx,
  Time,
  Util
} from 'yougile-util'
import {
  DataObject,
  DataObjectId,
  Entity,
  Hub,
  idFromUserEvent,
  noBoardContainerId,
  reportsContainerId,
  SandboxTree,
  stickersContainerId,
  StorageDepartmentHierarchy,
  SystemMessageUtils,
  TimelineFilter,
  TimelineFilterBackend,
  UserEvent,
  UserEventsByDay,
  UserEventsByTime
} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {UserEventsClientDataLimited} from '@client/user-events-client'
import {getUserProxyByUserId} from '@components/common/base/avatar/use-user-proxy'
import {MessageBox} from '@components/message-box'
import {ChatUtils} from '@extensions/chat-utils'
import {UserProxy} from '@extensions/proxies/user-proxy'
import {SubtaskUtil} from '@extensions/subtask-util'
import {dateFormat, dateWithTimeFormat} from '../stats-timeline-store'
import {getActionName} from './action-name'
import {getObjectName} from './object-name'

export const getTitle = (obj: Entity) =>
  obj ? obj.title.split('___deleted')[0] : ''

export const getProjectTitle = (userEvent: UserEvent) => {
  const box = Sync.getCurrentSandbox()
  const obj = box.getHub(idFromUserEvent(userEvent.obj))

  return getTitle(obj)
}

export const getPath = ({
  obj,
  box,
  withName = false,
  withoutLimit = false,
  prevParents = []
}: {
  obj: Hub | undefined
  box: SandboxTree
  withName?: boolean
  withoutLimit?: boolean
  prevParents?: DataObjectId[]
}): string => {
  if (!obj) {
    return ''
  }
  if (obj.id === noBoardContainerId || obj.location === noBoardContainerId) {
    const parents = SubtaskUtil.getParentTaskIds(obj.id)
    if (parents?.length) {
      const parent1 = box.getHub(parents[0])
      if (parent1 && !prevParents.includes(parent1.id)) {
        return getPath({
          obj: parent1,
          box,
          withName,
          withoutLimit,
          prevParents: [...prevParents, parent1.id]
        })
      }
    }
    return Lang.msg.taskPage.noBoard
  }
  const parentPath = box
    .getAllParentHubs(obj)
    .reverse()
    .slice(1)
    .map(p => (withoutLimit ? getTitle(p) : Util.limitStr(getTitle(p), 20)))
    .join(' » ')

  if (withName) {
    const suffix = parentPath.length > 0 ? ' » ' : ''
    return (
      parentPath +
      suffix +
      Util.limitStr(getTitle(obj), withoutLimit ? 1000 : 20)
    )
  } else {
    return parentPath
  }
}

interface Hint {
  event: UserEvent
  obj: DataObject
  withoutLimit?: boolean
}

export const getHint = (
  {event, obj, withoutLimit = false}: Hint,
  hierarchy: StorageDepartmentHierarchy
): string => {
  if (obj instanceof Hub) {
    const box = Sync.get(obj)

    if (event.type === 'message' || event.type === 'file') {
      return getPath({obj, box, withName: false, withoutLimit})
    } else if (event.type === 'move') {
      const objFrom = box.getHub(event.from)
      const isValidFrom = PermissionsChecker.checkShow(hierarchy, objFrom, {})
      const objTo = box.getHub(event.to)
      if (!objFrom && !objTo) {
        return '???'
      }
      return Lang.msg.statsTimeline.fromTo(
        isValidFrom
          ? getPath({obj: objFrom, box, withName: true, withoutLimit})
          : Lang.msg.statsTimeline.inaccessibleLocation,
        getPath({obj: objTo, box, withName: true, withoutLimit})
      )
    } else {
      return getPath({obj, box, withName: false, withoutLimit})
    }
  } else {
    return Lang.msg.sticker
  }
}

export const click = (event: UserEvent) => () => {
  const box = Sync.getCurrentSandbox()
  const obj = box.getHub(event.obj as DataObjectId)
  if (!obj) {
    return
  }
  const type = box.getType(obj)
  if (type !== 'task') {
    return
  }
  if (obj.deleted) {
    return MessageBox.warn(
      `${Util.capitalize(Lang.msg.statActionObjTask)} ${
        Lang.msg.statActionDeleteOther
      }`
    )
  }
  if (event.type === 'message') {
    ChatUtils.openChat(obj, {messageTime: event.time})
  } else if (event.type === 'file') {
    if (event.key === 'description') {
      ChatUtils.openChat(obj, {tab: 'description'})
    } else {
      ChatUtils.openChat(obj, {messageTime: event.key})
    }
  } else {
    ChatUtils.openChat(obj, {tab: 'connected'})
  }
}

export const downloadStatsTimelineXlsx = (events: UserEventsByTime) => {
  const box = Sync.getCurrentSandbox()
  const hierarchy = DepartmentsClient.getHierarchy(box)
  const rows = Object.keys(events)
    .filter(k => getUserProxyByUserId(events[+k].user))
    .map(k =>
      getRow(
        {
          event: events[+k],
          time: +k,
          user: getUserProxyByUserId(events[+k].user)
        },
        hierarchy
      )
    )

  const now = Time.now()
  const data: Array<Array<string | number>> = []
  const header = [
    Lang.msg.statsTimeline.dateTime,
    Lang.msg.statsTimeline.action,
    Lang.msg.statsTimeline.initiator,
    Lang.msg.statsTimeline.location,
    Lang.msg.statsTimeline.task
  ]
  data.push(header)
  saveAsXlsx(format(now, dateWithTimeFormat) + '.xlsx', data.concat(rows))
}

interface Props {
  user: UserProxy
  event: UserEvent
  time: number
}

const getRow = (props: Props, hierarchy: StorageDepartmentHierarchy) => {
  const {user, event, time} = props

  const box = Sync.getCurrentSandbox()
  const obj = box.getHub(idFromUserEvent(event.obj))
  if (!obj) {
    return null
  }
  const objType = box.getType(obj)

  const action = [
    getActionName({
      event,
      objType,
      isSticker: obj.location === stickersContainerId,
      obj
    }),
    getObjectName({event, obj})
  ].join(' ')

  return [
    format(time, dateWithTimeFormat),
    action,
    user.userName,
    getHint({event, obj, withoutLimit: true}, hierarchy),
    objType === 'task' ? getProjectTitle(event) : ''
  ]
}

export const createEmptyFilter = (): TimelineFilter => ({
  located: [],
  createdAt: null,
  createdBy: [],
  action: [],
  title: '',
  id: ''
})

export const getUserNamesInString = (usersIds: DataObjectId[]) => {
  return usersIds
    .map(id => getUserProxyByUserId(id))
    .map(user => user.userName)
    .join(', ')
}

export const eventsGroupByDay = (obj: UserEventsByTime): UserEventsByDay => {
  logger.log(`[Events] Grouping ${Object.keys(obj).length} events`)
  const arr = Object.keys(obj)
    .filter(k => {
      const proxy = getUserProxyByUserId(obj[+k].user)
      if (!proxy) {
        logger.log('[Events] No proxy for ', k, obj[+k].user)
      }
      return !!proxy
    })
    .map(k => ({
      day: format(+k, dateFormat),
      event: {
        [+k]: obj[+k]
      }
    }))
  return arr.reduce((acc: UserEventsByDay, item) => {
    acc[item.day] = {...(acc[item.day] ?? {}), ...item.event}
    return acc
  }, {})
}

export const eventsGroupByDayForTaskLog = (
  obj: UserEventsByTime
): UserEventsByDay => {
  const arr = Object.keys(obj).map(k => ({
    day: format(+k, dateFormat),
    event: {
      [+k]: obj[+k]
    }
  }))
  return arr.reduce((acc: UserEventsByDay, item) => {
    acc[item.day] = {...(acc[item.day] ?? {}), ...item.event}
    return acc
  }, {})
}

const filterLocationCheck = (
  dataObjectIds: DataObjectId[],
  events: UserEventsByTime,
  timestamp: number
): number => {
  if (!events[timestamp]) {
    return null
  }
  const isDataObj = dataObjectIds.length
    ? dataObjectIds.includes(events[timestamp].obj as DataObjectId) ||
      !!dataObjectIds.find(id =>
        SubtaskUtil.isParentOf(id, events[timestamp].obj as DataObjectId)
      )
    : true
  if (isDataObj) {
    return timestamp
  } else {
    return 0
  }
}

export const filterEvents = (events: UserEventsByTime) => {
  const box = Sync.getCurrentSandbox()
  const hierarchy = DepartmentsClient.getHierarchy(box)
  return ObjUtil.filter(events, (_, filter) => {
    const hub = box.getHub(filter.obj as DataObjectId)
    const parentHub = box.getAllParentHubs(hub).reverse().slice(1)[0]
    if (
      parentHub?.title?.startsWith('.') &&
      parentHub?.id !== noBoardContainerId
    ) {
      return false
    }
    if (!hub || !PermissionsChecker.checkShow(hierarchy, hub, {})) {
      return false
    }
    if (box.getType(hub) === 'task' && hub.data.private) {
      return false
    }
    if (filter.type === 'message') {
      return !SystemMessageUtils.isSystemMessage(
        hub.chat.messages.get(filter.time)
      )
    }
    return hub.location !== reportsContainerId
  })
}

export const getActiveFilter = (filter: TimelineFilter) => {
  const result = Object.keys(
    ObjUtil.filter(filter, (key, data) => {
      if (key === 'createdAt') {
        return !!filter?.createdAt?.start
      } else {
        return Array.isArray(data) && !!data.length
      }
    })
  )
  return result as Array<keyof TimelineFilter>
}

export const getFiltersForBackend = (
  filter: TimelineFilter,
  activeFilter: (keyof TimelineFilter)[]
) => {
  let backendFilter: TimelineFilterBackend = null
  const data = ObjUtil.filter(filter, key => activeFilter.includes(key))
  if (data.createdAt) {
    const dates = data.createdAt
    backendFilter = {
      ...data,
      createdAt: {
        start: relativeTimeToTimestamp(dates.start, dates.datesFrom),
        end: relativeTimeToTimestamp(dates.end, dates.datesFrom)
      }
    }
  } else {
    backendFilter = {
      ...data,
      createdAt: null
    }
  }
  return backendFilter
}

export const filterLocationStats = ({
  filter,
  stats,
  itemsToLoadLocation
}: {
  filter: TimelineFilter
  stats: UserEventsClientDataLimited
  itemsToLoadLocation: number
}) => {
  const res: UserEventsByTime = {}
  const dataObjectIds = filter.located.map(i => i.parent)
  const eventTimestamps = [...Object.keys(stats.events)].map(Number)
  const result: number[] = []
  let ixToCheck = 0
  while (result.length < itemsToLoadLocation + 1) {
    const data = filterLocationCheck(
      dataObjectIds,
      stats.events,
      eventTimestamps[ixToCheck]
    )
    if (data === null) {
      break
    }
    if (data !== 0) {
      result.push(data)
    }
    ixToCheck++
  }
  const resultSlice = result.slice(0, itemsToLoadLocation)
  resultSlice.forEach(t => (res[t] = stats.events[t]))
  const isEventsLeft = result.length > itemsToLoadLocation
  const filteredStats = {
    ...stats,
    events: res
  }
  return {filteredStats, isEventsLeft}
}
